import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import {
  createFeedback,
  getUserVote,
  listFeedback,
  toggleVote,
  type FeedbackItem,
  type FeedbackVoteValue,
} from '../../lib/feedback';
import FeedbackList from './FeedbackList';

const FEEDBACK_ROUTE_PREFIXES = ['/profile/edit'];

const isFeedbackRoute = (pathname: string) => {
  if (!pathname) return false;
  if (FEEDBACK_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }
  return pathname.startsWith('/profile') && pathname.includes('build');
};

const FeedbackDrawer: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [votesById, setVotesById] = useState<Record<string, FeedbackVoteValue | 0>>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [votingId, setVotingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';
  const shouldRender = useMemo(
    () => isFeedbackRoute(pathname),
    [pathname]
  );

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(''), 2500);
  };

  const loadFeedback = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const feedbackItems = await listFeedback(50);
      setItems(feedbackItems);

      const voteEntries = await Promise.all(
        feedbackItems.map(async (item) => {
          const vote = await getUserVote(item.id, user.uid);
          return [item.id, vote?.value || 0] as const;
        })
      );
      const votesMap: Record<string, FeedbackVoteValue | 0> = {};
      voteEntries.forEach(([id, value]) => {
        votesMap[id] = value;
      });
      setVotesById(votesMap);
    } catch (err) {
      console.error('Failed to load feedback:', err);
      setError('Unable to load feedback right now.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && user) {
      loadFeedback();
    }
  }, [isOpen, user, loadFeedback]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user) {
      setError('Please log in to post feedback.');
      return;
    }
    const trimmed = feedbackText.trim();
    if (trimmed.length < 5) {
      setError('Feedback must be at least 5 characters.');
      return;
    }
    if (trimmed.length > 800) {
      setError('Feedback must be 800 characters or less.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await createFeedback({
        text: trimmed,
        authorId: user.uid,
        context: { route: pathname },
      });
      setFeedbackText('');
      showToast('Thanks — posted anonymously.');
      await loadFeedback();
    } catch (err) {
      console.error('Failed to post feedback:', err);
      setError('Unable to post feedback right now.');
    } finally {
      setSubmitting(false);
    }
  };

  const applyVoteUpdate = (
    item: FeedbackItem,
    currentVote: FeedbackVoteValue | 0,
    desired: FeedbackVoteValue
  ) => {
    if (desired !== 1 && desired !== -1) {
      return {
        upvoteCount: item.upvoteCount,
        downvoteCount: item.downvoteCount,
        score: item.upvoteCount - item.downvoteCount,
        nextVote: currentVote,
      };
    }

    let upvoteCount = item.upvoteCount;
    let downvoteCount = item.downvoteCount;
    let score = item.score;
    let nextVote: FeedbackVoteValue | 0 = currentVote;

    if (currentVote === desired) {
      if (desired === 1) {
        upvoteCount -= 1;
        score -= 1;
      } else {
        downvoteCount -= 1;
        score += 1;
      }
      nextVote = 0;
    } else if (currentVote === 0) {
      if (desired === 1) {
        upvoteCount += 1;
        score += 1;
        nextVote = 1;
      } else {
        downvoteCount += 1;
        score -= 1;
        nextVote = -1;
      }
    } else {
      if (desired === 1) {
        downvoteCount -= 1;
        upvoteCount += 1;
        score += 2;
        nextVote = 1;
      } else {
        upvoteCount -= 1;
        downvoteCount += 1;
        score -= 2;
        nextVote = -1;
      }
    }

    upvoteCount = Math.max(0, upvoteCount);
    downvoteCount = Math.max(0, downvoteCount);
    score = upvoteCount - downvoteCount;

    return {
      upvoteCount,
      downvoteCount,
      score,
      nextVote,
    };
  };

  const handleVote = async (feedbackId: string, desired: FeedbackVoteValue) => {
    if (!user) {
      setError('Please log in to vote.');
      return;
    }

    const currentVote = votesById[feedbackId] || 0;
    const currentItem = items.find((item) => item.id === feedbackId);
    if (!currentItem) return;

    const optimistic = applyVoteUpdate(currentItem, currentVote, desired);
    setItems((prev) =>
      prev.map((item) =>
        item.id === feedbackId
          ? {
              ...item,
              upvoteCount: optimistic.upvoteCount,
              downvoteCount: optimistic.downvoteCount,
              score: optimistic.score,
            }
          : item
      )
    );
    setVotesById((prev) => ({ ...prev, [feedbackId]: optimistic.nextVote }));

    setVotingId(feedbackId);
    try {
      const result = await toggleVote(feedbackId, user.uid, desired);
      setItems((prev) =>
        prev.map((item) =>
          item.id === feedbackId
            ? {
                ...item,
                upvoteCount: result.upvoteCount,
                downvoteCount: result.downvoteCount,
                score: result.score,
              }
            : item
        )
      );
      setVotesById((prev) => ({ ...prev, [feedbackId]: result.userVote }));
    } catch (err) {
      console.error('Failed to vote:', err);
      setItems((prev) =>
        prev.map((item) => (item.id === feedbackId ? currentItem : item))
      );
      setVotesById((prev) => ({ ...prev, [feedbackId]: currentVote }));
      setError('Unable to update your vote. Please try again.');
    } finally {
      setVotingId(null);
    }
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed left-4 bottom-4 z-40 rounded-full bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-teal-700 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2"
      >
        Feedback
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Feedback Forum
                </p>
                <p className="text-xs text-slate-500">
                  Share ideas to improve InfluenceHub.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="border-b border-slate-200 px-4 py-3">
              {user ? (
                <form onSubmit={handleSubmit} className="space-y-2">
                  <textarea
                    value={feedbackText}
                    onChange={(event) => setFeedbackText(event.target.value)}
                    placeholder="What should we improve or add next?"
                    className="h-24 w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                      Anonymous to other creators.
                    </span>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="rounded-md bg-teal-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {submitting ? 'Posting...' : 'Post feedback'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  Please log in to view and post feedback.
                </div>
              )}

              {error && (
                <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {error}
                </div>
              )}
              {toast && (
                <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                  {toast}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {user ? (
                loading ? (
                  <div className="text-sm text-slate-500">
                    Loading feedback...
                  </div>
                ) : (
                  <FeedbackList
                    items={items}
                    votesById={votesById}
                    onVote={handleVote}
                    votingId={votingId}
                  />
                )
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackDrawer;
